import { useMemo, useState } from "react";
import { useMyConsentTasks } from "../../../../hooks/useMyConsentTasks";
import { useResolveWorklistAlert } from "../../../../hooks/useResolveWorklistAlert";
import UiModalAlert from "../../../../components/modal-alert/ModalAlert.component";
import { Task } from "../../../../models/entities/task.model";
import { getSessionStorageUser } from "../../../../services/session/auth.service";
import WorklistSection from "../WorklistSection";
import { useWorklist } from "../WorklistContext";
import { sortConsentTasks } from "../worklist.service";
import CriticalAlertRow from "../critical-alerts/CriticalAlertRow";

export const CONSENT_SECTION_ID = "consent";

/**
 * "Consent needed" — a worklist section under Critical alerts (KAN-2647, Maria 2026-10-15).
 *
 * Shows the three consent task rules (telehealth consent Prospective LOW, telehealth
 * consent Active HIGH, ToS + PP HIGH), assigned to the logged-in user, ordered HIGH
 * first then oldest, with a blue edge and same Call/Edit/Resolve actions as critical
 * alerts.
 *
 * NOTE: consent tasks have no retrigger logic, so a manual Resolve while consent is
 * still missing hides that participant's task permanently. Tasks auto-resolve when
 * consent is recorded. Resolve is kept anyway for consistency with the other
 * sections (Maria, 2026-10-15 16:04Z).
 *
 * Plan: karchitecture/planning/kan-2647-consent-alert-homepage.md.
 */
const ConsentSection = () => {
	const sessionUser = getSessionStorageUser();
	const { alerts, count, isLoading, isError, invalidate, hideAlert } = useMyConsentTasks(
		sessionUser?.id
	);
	// See useResolveWorklistAlert for resolve logic and optimistic-hide details.
	const { resolve, isPending } = useResolveWorklistAlert(hideAlert, sessionUser?.id);
	const { getRowOffset } = useWorklist();

	// HIGH first (priority) then oldest (age).
	const sortedTasks = useMemo(() => sortConsentTasks(alerts), [alerts]);

	const [alertModal, setAlertModal] = useState<{ entity: Task | null; isVisible: boolean }>({
		entity: null,
		isVisible: false,
	});

	const edit = (alert: Task) => setAlertModal({ entity: alert, isVisible: true });
	const cancelAlertModal = () => setAlertModal((prev) => ({ ...prev, isVisible: false }));
	// The modal's own save invalidates the shared task query keys; invalidate()
	// additionally refreshes this section's count.
	const completeAlertModal = () => {
		setAlertModal((prev) => ({ ...prev, isVisible: false }));
		invalidate();
	};

	const rowOffset = getRowOffset(CONSENT_SECTION_ID);

	return (
		<>
			<WorklistSection
				sectionId={CONSENT_SECTION_ID}
				titleKey="HOME.WORKLIST.CONSENT.TITLE"
				loadingMessageKey="HOME.WORKLIST.CONSENT.LOADING"
				errorMessageKey="HOME.WORKLIST.CONSENT.ERROR"
				count={count}
				isLoading={isLoading}
				isError={isError}
				isEmpty={sortedTasks.length === 0}
			>
				<ul className="worklist-rows">
					{sortedTasks.map((alert, position) => (
						<CriticalAlertRow
							key={alert.id}
							alert={alert}
							variant="consent"
							index={rowOffset + position + 1}
							onResolve={resolve}
							onEdit={edit}
							isResolving={isPending}
						/>
					))}
				</ul>
			</WorklistSection>
			{alertModal.isVisible && alertModal.entity && (
				<UiModalAlert
					entity={alertModal.entity}
					isVisible={alertModal.isVisible}
					onCancel={cancelAlertModal}
					onComplete={completeAlertModal}
				/>
			)}
		</>
	);
};

export default ConsentSection;
